import { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "@/firebase";

type ConditionField = "subject" | "from" | "fromDomain";
type ConditionOperator = "contains" | "equals" | "startsWith" | "endsWith";
type ConditionLogic = "and" | "or";
type FolderTarget = "inbox" | "archive" | "spam" | "label";
type InboundStatus = "open" | "processing" | "done";

interface RuleCondition {
  field: ConditionField;
  operator: ConditionOperator;
  value: string;
}

interface RuleActions {
  folder: FolderTarget | null;
  label: string | null;
  setStatus: InboundStatus | null;
}

interface MailRule {
  id: string;
  name: string;
  enabled: boolean;
  priority: number;
  conditionLogic: ConditionLogic;
  conditions: RuleCondition[];
  actions: RuleActions;
}

const FIELD_LABELS: Record<ConditionField, string> = {
  subject: "件名",
  from: "送信元メールアドレス",
  fromDomain: "送信元ドメイン",
};

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  contains: "を含む",
  equals: "と一致する",
  startsWith: "で始まる",
  endsWith: "で終わる",
};

const FOLDER_LABELS: Record<FolderTarget, string> = {
  inbox: "受信トレイ（振り分けなし）",
  archive: "アーカイブ",
  spam: "迷惑メール",
  label: "アーカイブ（ラベル指定）",
};

const STATUS_LABELS: Record<InboundStatus, string> = {
  open: "未処理",
  processing: "対応中",
  done: "処理済み",
};

const emptyCondition = (): RuleCondition => ({ field: "subject", operator: "contains", value: "" });
const emptyRule = (): Omit<MailRule, "id"> => ({
  name: "",
  enabled: true,
  priority: 10,
  conditionLogic: "and",
  conditions: [emptyCondition()],
  actions: { folder: null, label: null, setStatus: null },
});

export default function MailRulesPage() {
  const navigate = useNavigate();
  const [rules, setRules] = useState<MailRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState<Omit<MailRule, "id"> | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<{ ruleId: string; message: string } | null>(null);

  // 一括処理済み設定
  const [bulkBefore, setBulkBefore] = useState("2026-06-30T10:00");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  const handleBulkDone = async () => {
    if (bulkRunning) return;
    // datetime-local の値はブラウザがローカル時間（JST）として解釈しUTCに変換済み
    const localDate = new Date(bulkBefore);
    if (isNaN(localDate.getTime())) { setBulkResult("日時の形式が不正です"); return; }
    const threshold = Timestamp.fromDate(localDate);

    const confirmMsg = `${bulkBefore} JST より前の受信メールをすべて「処理済み」にします。\nよろしいですか？`;
    if (!confirm(confirmMsg)) return;

    setBulkRunning(true);
    setBulkResult(null);
    try {
      const snap = await getDocs(
        query(
          collection(db, "mail_messages"),
          where("direction", "==", "in"),
          where("createdAt", "<", threshold)
        )
      );
      const targets = snap.docs.filter((d) => d.data().status !== "done");
      if (targets.length === 0) {
        setBulkResult("対象メールはありませんでした（すでに処理済み）");
        return;
      }
      let updated = 0;
      for (let i = 0; i < targets.length; i += 500) {
        const batch = writeBatch(db);
        targets.slice(i, i + 500).forEach((d) => batch.update(d.ref, { status: "done" }));
        await batch.commit();
        updated += Math.min(500, targets.length - i);
      }
      setBulkResult(`完了：${updated}件を処理済みにしました`);
    } catch (err) {
      console.error("一括処理済みエラー:", err);
      setBulkResult(`エラー: ${err instanceof Error ? err.message : "不明"}`);
    } finally {
      setBulkRunning(false);
    }
  };

  const loadRules = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "mail_rules"), orderBy("priority", "asc"))
      );
      setRules(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<MailRule, "id">) })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRules(); }, []);

  const openNew = () => {
    setEditingId(null);
    setEditingRule(emptyRule());
  };

  const openEdit = (rule: MailRule) => {
    const { id, ...rest } = rule;
    setEditingId(id);
    setEditingRule(JSON.parse(JSON.stringify(rest)));
  };

  const closeEditor = () => { setEditingRule(null); setEditingId(null); };

  const handleSave = async () => {
    if (!editingRule) return;
    if (!editingRule.name.trim()) { setSaveError("ルール名を入力してください"); return; }
    if (editingRule.conditions.some((c) => !c.value.trim())) {
      setSaveError("すべての条件に値を入力してください"); return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (editingId) {
        await updateDoc(doc(db, "mail_rules", editingId), { ...editingRule, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "mail_rules"), { ...editingRule, createdAt: serverTimestamp() });
      }
      await loadRules();
      closeEditor();
    } catch (err) {
      console.error("ルール保存エラー:", err);
      setSaveError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("このルールを削除しますか？")) return;
    await deleteDoc(doc(db, "mail_rules", id));
    await loadRules();
  };

  const handleToggle = async (rule: MailRule) => {
    await updateDoc(doc(db, "mail_rules", rule.id), { enabled: !rule.enabled });
    setRules((prev) => prev.map((r) => r.id === rule.id ? { ...r, enabled: !r.enabled } : r));
  };

  const handleApplyToPast = async (rule: MailRule) => {
    if (applyingId) return;
    if (!confirm(`「${rule.name}」を過去の受信メール（受信トレイ）に適用しますか？\n条件に一致するメールのフォルダ・ステータスが変更されます。`)) return;

    setApplyingId(rule.id);
    setApplyResult(null);
    try {
      // 受信トレイのメール全件取得（folder=inbox または folder未設定）
      const snap = await getDocs(
        query(collection(db, "mail_messages"), where("direction", "==", "in"))
      );
      const inboxDocs = snap.docs.filter((d) => {
        const f = d.data().folder;
        return !f || f === "inbox";
      });

      // 条件評価（fetchMails.js と同じロジック）
      const matchDoc = (data: Record<string, unknown>) => {
        const subject = ((data.subject as string) ?? "").toLowerCase();
        const fromAddr = ((data.from as string) ?? "").toLowerCase();
        const fromDomain = fromAddr.includes("@") ? fromAddr.split("@")[1] : "";
        const results = (rule.conditions ?? []).map(({ field, operator, value }) => {
          const v = (value ?? "").toLowerCase();
          const target = field === "subject" ? subject : field === "from" ? fromAddr : fromDomain;
          if (operator === "contains")   return target.includes(v);
          if (operator === "equals")     return target === v;
          if (operator === "startsWith") return target.startsWith(v);
          if (operator === "endsWith")   return target.endsWith(v);
          return false;
        });
        return rule.conditionLogic === "or" ? results.some(Boolean) : results.every(Boolean);
      };

      const matched = inboxDocs.filter((d) => matchDoc(d.data() as Record<string, unknown>));

      if (matched.length === 0) {
        setApplyResult({ ruleId: rule.id, message: "条件に一致するメールはありませんでした" });
        return;
      }

      // アクション決定
      let folder = "inbox";
      let label: string | null = null;
      let status: string | null = null;
      if (rule.actions.folder === "archive" || rule.actions.folder === "spam") {
        folder = rule.actions.folder;
        label  = rule.actions.label ?? null;
        status = "done";
      } else if (rule.actions.folder === "label" && rule.actions.label) {
        folder = "archive";
        label  = rule.actions.label;
        status = "done";
      } else if (rule.actions.setStatus && rule.actions.folder === "inbox") {
        status = rule.actions.setStatus;
      }

      // Firestore batch update（500件制限対応）
      let updated = 0;
      for (let i = 0; i < matched.length; i += 500) {
        const batch = writeBatch(db);
        matched.slice(i, i + 500).forEach((d) => {
          const patch: Record<string, unknown> = { folder };
          if (label !== null) patch.label = label;
          if (status !== null) patch.status = status;
          batch.update(d.ref, patch);
        });
        await batch.commit();
        updated += Math.min(500, matched.length - i);
      }

      setApplyResult({ ruleId: rule.id, message: `${updated}件に適用しました` });
    } catch (err) {
      console.error("過去メール適用エラー:", err);
      setApplyResult({ ruleId: rule.id, message: `エラー: ${err instanceof Error ? err.message : "不明"}` });
    } finally {
      setApplyingId(null);
      setTimeout(() => setApplyResult(null), 5000);
    }
  };

  const setCondition = (index: number, patch: Partial<RuleCondition>) => {
    if (!editingRule) return;
    const conditions = [...editingRule.conditions];
    conditions[index] = { ...conditions[index], ...patch };
    setEditingRule({ ...editingRule, conditions });
  };

  const addCondition = () => {
    if (!editingRule) return;
    setEditingRule({ ...editingRule, conditions: [...editingRule.conditions, emptyCondition()] });
  };

  const removeCondition = (index: number) => {
    if (!editingRule || editingRule.conditions.length <= 1) return;
    const conditions = editingRule.conditions.filter((_, i) => i !== index);
    setEditingRule({ ...editingRule, conditions });
  };

  const inputCls = "rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300";
  const selectCls = `${inputCls} bg-white`;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* ヘッダー */}
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate("/admin/mail")} className="text-sm text-blue-600 hover:underline">
              ← メール管理へ戻る
            </button>
            <h1 className="mt-1 text-2xl font-bold text-gray-800">振り分けルール</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              メール受信時に自動でフォルダ・ステータスを振り分けるルールを設定します
            </p>
          </div>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            ＋ ルール追加
          </button>
        </div>

        {/* ルール一覧 */}
        <div className="bg-white rounded-xl shadow-sm divide-y">
          {loading && <div className="p-8 text-center text-sm text-gray-400">読み込み中...</div>}
          {!loading && rules.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-400">ルールがありません</div>
          )}
          {rules.map((rule) => (
            <div key={rule.id} className="flex items-start gap-4 px-5 py-4">
              {/* 有効/無効トグル */}
              <button
                onClick={() => handleToggle(rule)}
                className={`mt-0.5 shrink-0 w-10 h-5 rounded-full transition-colors ${rule.enabled ? "bg-blue-500" : "bg-gray-300"}`}
                title={rule.enabled ? "有効（クリックで無効化）" : "無効（クリックで有効化）"}
              >
                <span className={`block w-4 h-4 rounded-full bg-white shadow transition-transform mx-0.5 ${rule.enabled ? "translate-x-5" : ""}`} />
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-800">{rule.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    優先度 {rule.priority}
                  </span>
                  {!rule.enabled && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">無効</span>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                  <div>
                    条件（{rule.conditionLogic === "and" ? "すべて一致" : "いずれか一致"}）：
                    {rule.conditions.map((c, i) => (
                      <span key={i} className="ml-1">
                        {FIELD_LABELS[c.field]}「{c.value}」{OPERATOR_LABELS[c.operator]}
                        {i < rule.conditions.length - 1 ? ` ${rule.conditionLogic.toUpperCase()} ` : ""}
                      </span>
                    ))}
                  </div>
                  <div>
                    アクション：
                    {rule.actions.folder && <span className="ml-1">→ {FOLDER_LABELS[rule.actions.folder]}</span>}
                    {rule.actions.label && <span className="ml-1">（ラベル: {rule.actions.label}）</span>}
                    {rule.actions.setStatus && rule.actions.folder === "inbox" && (
                      <span className="ml-1">ステータス: {STATUS_LABELS[rule.actions.setStatus]}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="shrink-0 flex flex-col items-end gap-1">
                <div className="flex gap-2">
                  <button
                    onClick={() => openEdit(rule)}
                    className="text-xs text-blue-600 hover:underline"
                  >編集</button>
                  <button
                    onClick={() => handleDelete(rule.id)}
                    className="text-xs text-red-500 hover:underline"
                  >削除</button>
                </div>
                <button
                  onClick={() => handleApplyToPast(rule)}
                  disabled={!!applyingId}
                  className="text-xs text-purple-600 hover:underline disabled:opacity-40"
                >
                  {applyingId === rule.id ? "適用中..." : "📬 過去のメールに適用"}
                </button>
                {applyResult?.ruleId === rule.id && (
                  <span className="text-xs text-green-600 font-semibold">{applyResult.message}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 一括処理済み設定 */}
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-3">
          <h2 className="font-semibold text-gray-800">一括処理済み設定</h2>
          <p className="text-sm text-gray-500">
            指定した日時より前に受信したメール（受信トレイ）をすべて「処理済み」に変更します。
          </p>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500">この日時より前（JST）</label>
              <input
                type="datetime-local"
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={bulkBefore}
                onChange={(e) => setBulkBefore(e.target.value)}
              />
            </div>
            <button
              onClick={handleBulkDone}
              disabled={bulkRunning}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
            >
              {bulkRunning ? "処理中..." : "✓ 一括処理済みにする"}
            </button>
          </div>
          {bulkResult && (
            <p className={`text-sm font-semibold px-3 py-2 rounded-lg ${
              bulkResult.startsWith("エラー") ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"
            }`}>
              {bulkResult}
            </p>
          )}
        </div>
      </div>

      {/* 編集モーダル */}
      {editingRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="font-bold text-gray-800">{editingId ? "ルール編集" : "ルール追加"}</h2>
              <button onClick={closeEditor} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-6 space-y-5">
              {/* 基本情報 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1">
                  <label className="text-xs font-semibold text-gray-500">ルール名 *</label>
                  <input
                    className={`${inputCls} w-full`}
                    value={editingRule.name}
                    onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
                    placeholder="例: 広告メール自動アーカイブ"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-500">優先度（小さい順に評価）</label>
                  <input
                    type="number"
                    className={`${inputCls} w-full`}
                    value={editingRule.priority}
                    onChange={(e) => setEditingRule({ ...editingRule, priority: Number(e.target.value) })}
                    min={1}
                    max={999}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-500">条件の結合</label>
                  <select
                    className={`${selectCls} w-full`}
                    value={editingRule.conditionLogic}
                    onChange={(e) => setEditingRule({ ...editingRule, conditionLogic: e.target.value as ConditionLogic })}
                  >
                    <option value="and">AND（すべての条件を満たす）</option>
                    <option value="or">OR（いずれかの条件を満たす）</option>
                  </select>
                </div>
              </div>

              {/* 条件リスト */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500">条件</label>
                  <button
                    onClick={addCondition}
                    className="text-xs text-blue-600 hover:underline"
                  >＋ 条件を追加</button>
                </div>
                {editingRule.conditions.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap">
                    <select
                      className={`${selectCls} flex-1 min-w-[140px]`}
                      value={c.field}
                      onChange={(e) => setCondition(i, { field: e.target.value as ConditionField })}
                    >
                      {(Object.keys(FIELD_LABELS) as ConditionField[]).map((f) => (
                        <option key={f} value={f}>{FIELD_LABELS[f]}</option>
                      ))}
                    </select>
                    <select
                      className={`${selectCls} flex-1 min-w-[120px]`}
                      value={c.operator}
                      onChange={(e) => setCondition(i, { operator: e.target.value as ConditionOperator })}
                    >
                      {(Object.keys(OPERATOR_LABELS) as ConditionOperator[]).map((op) => (
                        <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
                      ))}
                    </select>
                    <input
                      className={`${inputCls} flex-1 min-w-[120px]`}
                      value={c.value}
                      onChange={(e) => setCondition(i, { value: e.target.value })}
                      placeholder="値を入力"
                    />
                    {editingRule.conditions.length > 1 && (
                      <button onClick={() => removeCondition(i)} className="text-gray-400 hover:text-red-500">✕</button>
                    )}
                  </div>
                ))}
              </div>

              {/* アクション */}
              <div className="space-y-3 rounded-xl bg-gray-50 p-4">
                <label className="text-xs font-semibold text-gray-500">アクション（一致した場合）</label>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">移動先フォルダ</label>
                  <select
                    className={`${selectCls} w-full`}
                    value={editingRule.actions.folder ?? ""}
                    onChange={(e) => {
                      const folder = (e.target.value || null) as FolderTarget | null;
                      setEditingRule({
                        ...editingRule,
                        actions: { ...editingRule.actions, folder, label: folder !== "label" ? null : editingRule.actions.label },
                      });
                    }}
                  >
                    <option value="">振り分けなし（受信トレイへ）</option>
                    {(Object.keys(FOLDER_LABELS) as FolderTarget[]).filter(f => f !== 'inbox').map((f) => (
                      <option key={f} value={f}>{FOLDER_LABELS[f]}</option>
                    ))}
                  </select>
                </div>

                {(editingRule.actions.folder === "archive" || editingRule.actions.folder === "label") && (
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">
                      ラベル名（アーカイブのサブフォルダ）
                    </label>
                    <input
                      className={`${inputCls} w-full`}
                      value={editingRule.actions.label ?? ""}
                      onChange={(e) => setEditingRule({
                        ...editingRule,
                        actions: { ...editingRule.actions, label: e.target.value || null },
                      })}
                      placeholder="例: 広告、お知らせ、請求書..."
                    />
                  </div>
                )}

                {(!editingRule.actions.folder || editingRule.actions.folder === "inbox") && (
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">ステータスを変更</label>
                    <select
                      className={`${selectCls} w-full`}
                      value={editingRule.actions.setStatus ?? ""}
                      onChange={(e) => setEditingRule({
                        ...editingRule,
                        actions: { ...editingRule.actions, setStatus: (e.target.value || null) as InboundStatus | null },
                      })}
                    >
                      <option value="">変更しない（未処理のまま）</option>
                      {(Object.keys(STATUS_LABELS) as InboundStatus[]).map((s) => (
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                      ))}
                    </select>
                  </div>
                )}

                {(editingRule.actions.folder === "archive" || editingRule.actions.folder === "spam" || editingRule.actions.folder === "label") && (
                  <p className="text-xs text-blue-600">
                    ※ アーカイブ・迷惑メールに振り分けたメールは自動で「処理済み・既読」が付与され、受信トレイには表示されません
                  </p>
                )}
              </div>
            </div>

            <div className="border-t px-6 py-4 space-y-2">
              {saveError && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{saveError}</p>
              )}
              <div className="flex justify-end gap-3">
                <button onClick={closeEditor} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg border">
                  キャンセル
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                >
                  {saving ? "保存中..." : "保存する"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
